import type { OpenAITool } from '../types';
import { NetworkError } from '@workspace/network';
import { COLD_START_RETRY, llmApiClient } from './network';

export interface FetchToolsOptions {
  apiUrl: string;
  apiKey?: string;
}

/**
 * Fetch available MCP tools from the LLM load balancer
 * Returns tools in OpenAI-compatible format for use in the tools listing UI
 */
export async function fetchTools({ apiUrl, apiKey }: FetchToolsOptions): Promise<OpenAITool[]> {
  try {
    const result = await llmApiClient.safeRequest<{ tools: OpenAITool[] }>(
      `${apiUrl}/v1/tools`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey && { Authorization: `Bearer ${apiKey}` }),
        },
        // Read-only, and it runs at startup alongside the model list — same
        // cold-start window, same reason to ride it out.
        retry: COLD_START_RETRY,
      }
    );

    if (!result.ok) {
      // 503 means MCP is not configured - return empty tools (not an error)
      if (result.error instanceof NetworkError && result.error.status === 503) {
        return [];
      }
      throw new Error(`Failed to fetch tools: ${result.error.message}`);
    }

    return result.data.tools || [];
  } catch (error) {
    // Network errors or MCP not available - return empty array
    console.warn('[tools] Failed to fetch tools:', error);
    return [];
  }
}
